import { z } from "zod";

/**
 * SÖZLEŞME BAZLI HUKUKİ HİZMET ATAMASI (CLOS-1185).
 *
 * ⚠ Sağlayıcı sözleşmenin TARAFI DEĞİLDİR; bir tarafın ADINA çalışır. Şema bu ayrımı
 * taşıyor: atama `servedPartyId` ile kanonik taraf kaydına bağlanıyor, sağlayıcı ise ayrı
 * bir alanda duruyor ve hiçbir yerde taraf listesine karışmıyor.
 */
export const CONTRACT_SERVICE_ASSIGNMENT_STATUSES = [
  "pending_service_acceptance",
  "active",
  "suspended",
  "ended",
  "cancelled",
] as const;
export type ContractServiceAssignmentStatus = (typeof CONTRACT_SERVICE_ASSIGNMENT_STATUSES)[number];

export const CONTRACT_SERVICE_ASSIGNMENT_STATUS_NAMES: Record<
  ContractServiceAssignmentStatus,
  string
> = {
  pending_service_acceptance: "Hizmet kabulü bekleniyor",
  active: "Aktif",
  suspended: "Askıda",
  ended: "Sonlandırıldı",
  cancelled: "İptal edildi",
};

/**
 * ⚠ `scope` ATAMANIN KENDİ TAVANI — hizmet alanın yetkisinin kopyası DEĞİL. Etkin yetki
 * her istekte dört çarpanın kesişiminden kuruluyor (`intersectProviderCapability`).
 */
export const createContractServiceAssignmentSchema = z.object({
  servedPartyId: z.string().uuid("Hizmet alan taraf seçilmelidir"),
  serviceRelationshipId: z.string().uuid("Hizmet ilişkisi seçilmelidir"),
  scope: z
    .array(z.string().min(1))
    .min(1, "Atama kapsamı en az bir yetki içermelidir")
    .max(32, "Atama kapsamı en fazla 32 yetki içerebilir"),
});
export type CreateContractServiceAssignmentInput = z.infer<
  typeof createContractServiceAssignmentSchema
>;

/**
 * YENİ SAĞLAYICI DAVETİ — SÖZLEŞMEDEN BAŞLAYAN HİZMET İLİŞKİSİ (rapor §7).
 *
 * ⚠ Bu bir ContractParty daveti DEĞİL. Sağlayıcı sözleşmenin tarafı olmuyor; davet
 * kabul edildiğinde doğan şey bir HİZMET İLİŞKİSİ ve ona bağlı bir atama.
 *
 * ⚠ `scope` daveti gönderirken belirleniyor ve kabul anında yeniden yetkilendiriliyor:
 * kabul geciktiyse ve hizmet alanın tavanı daralmışsa kapsam o tavanı aşamaz.
 */
export const inviteContractServiceProviderSchema = z.object({
  servedPartyId: z.string().uuid("Hizmet alan taraf seçilmelidir"),
  email: z.email("Geçerli bir e-posta adresi giriniz"),
  scope: z
    .array(z.string().min(1))
    .min(1, "Atama kapsamı en az bir yetki içermelidir")
    .max(32, "Atama kapsamı en fazla 32 yetki içerebilir"),
});
export type InviteContractServiceProviderInput = z.infer<
  typeof inviteContractServiceProviderSchema
>;

/**
 * DAVETİ KABUL — sağlayıcı bağlamı AÇIKÇA seçilebilir.
 *
 * ⚠ `providerTenantId` isteğe bağlı: tek uygun bağlam varsa gerekmiyor. Birden fazla varsa
 * sunucu açık seçim istiyor — `first()` ile kapatmak, kullanıcıyı yanlış kimlikle
 * görevlendirebilirdi.
 */
export const acceptContractServiceInvitationSchema = z.object({
  providerTenantId: z.string().uuid("Geçerli bir çalışma alanı seçilmelidir").nullish(),
});

/**
 * SAĞLAYICI İÇİ DAĞITIM — büronun kendi ekibine (CLOS-1185).
 *
 * ⚠ Hedef bir ÜYELİK, kullanıcı değil: erişim kişinin o organizasyondaki üyeliğine bağlı
 * ve üyelik bittiğinde kaynağı kuruyor.
 */
export const distributeAssignedContractSchema = z.object({
  membershipId: z.string().uuid("Bir ekip üyesi seçilmelidir"),
});

/** ⚠ Gerekçe zorunlu: hukuki bir görevlendirme sessizce bitmez. */
export const endContractServiceAssignmentSchema = z.object({
  reason: z.string().min(3, "Sonlandırma gerekçesi yazılmalıdır").max(500),
});

/**
 * Sonlandırma ucunun yol parametreleri.
 *
 * ⚠ `idParamSchema` YETMEZ: bilinmeyen alanı reddeden şema, iki parametreli bir yolda
 * `assignmentId`'yi fazlalık sayıp 422 üretir (`contractRevisionParamsSchema` emsali).
 */
export const contractServiceAssignmentParamsSchema = z.object({
  id: z.uuid("Geçerli bir sözleşme seçilmelidir"),
  assignmentId: z.uuid("Geçerli bir görevlendirme seçilmelidir"),
});

export type ContractServiceAssignmentView = {
  id: string;
  contractId: string;
  servedPartyId: string;
  servedPartyName: string;
  serviceRelationshipId: string;
  providerTenantId: string | null;
  providerName: string | null;
  scope: string[];
  status: ContractServiceAssignmentStatus;
  originType: "existing_relationship" | "contract_invite";
  assignedByName: string;
  activatedAt: string | null;
  endedAt: string | null;
  createdAt: string;
};
